import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  Radio,
  RadioGroup,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { alpha } from '@mui/material/styles';
import type { RootState } from '../../../store';
import { formatarBRL } from '../../../lib/formato';
import {
  useAdminAtualizarConfiguracaoMutation,
  useAdminAtualizarTaxaBairroMutation,
  useAdminCriarTaxaBairroMutation,
  useAdminImportarTaxasSischefMutation,
  useAdminListarMunicipiosAtendidosQuery,
  useAdminListarTaxasBairroQuery,
  useAdminListarTaxasImportaveisSischefQuery,
  useAdminDiagnosticoKmQuery,
  useAdminObterConfiguracaoQuery,
  useLazyAdminDiagnosticoKmQuery,
  type TaxaEntregaBairroLoja,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { PainelInfo } from '../../../components/admin/painel-info';
import { BotaoConfigurarGoogleMaps } from '../../../components/admin/botao-configurar-google-maps';
import { PaginacaoTabela, useUltimaPagina } from '../../../components/admin/paginacao-tabela';
import { FaixasKm } from './faixas-km';

interface Editor {
  modo: 'criar' | 'editar';
  taxa?: TaxaEntregaBairroLoja;
  nomeBairro: string;
  /** ID do município atendido escolhido no combo. */
  idMunicipio: number | '';
  valor: string;
  ativo: boolean;
}



function formatarValor(s: string): string {
  const n = parseFloat(s);
  return Number.isFinite(n) ? formatarBRL(n) : '—';
}

export function AdminTaxaEntregaPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const { data, isLoading } = useAdminListarTaxasBairroQuery({ idConfig, pagina: pagina + 1, tamanhoPagina });
  const { itens, total } = useUltimaPagina(data);
  const { data: municipios } = useAdminListarMunicipiosAtendidosQuery(idConfig);
  const { data: configuracao } = useAdminObterConfiguracaoQuery(idConfig);
  const [salvarConfiguracao, statusEstrategia] = useAdminAtualizarConfiguracaoMutation();
  const porKm = configuracao?.estrategiaTaxa === 'KM';
  // A cobrança por distância depende da integração Google Maps da empresa —
  // testada de verdade, porque chave de navegador (com restrição de referrer)
  // é aceita no cadastro e recusada na chamada de servidor.
  const { data: diagnostico, isFetching: diagnosticando } = useAdminDiagnosticoKmQuery({ idConfig });
  const [retestar] = useLazyAdminDiagnosticoKmQuery();
  const [criar, statusCriar] = useAdminCriarTaxaBairroMutation();
  const [atualizar, statusAtualizar] = useAdminAtualizarTaxaBairroMutation();

  const [editor, setEditor] = useState<Editor | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // ===== Importação das taxas ENTREGA do Sischef =====
  const [importAberto, setImportAberto] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [erroImport, setErroImport] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const {
    data: importaveis,
    isFetching: carregandoImportaveis,
    error: erroCarregarImportaveis,
  } = useAdminListarTaxasImportaveisSischefQuery(idConfig, { skip: !importAberto });
  const [importar, statusImportar] = useAdminImportarTaxasSischefMutation();

  function abrirImportar(): void {
    setErroImport(null);
    setSelecionados(new Set());
    setImportAberto(true);
  }

  function fecharImportar(): void {
    setImportAberto(false);
    setErroImport(null);
  }

  function alternarSelecao(idProduto: number): void {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(idProduto)) proximo.delete(idProduto);
      else proximo.add(idProduto);
      return proximo;
    });
  }

  function alternarTodos(): void {
    const itens = importaveis?.itens ?? [];
    setSelecionados((atual) => (atual.size === itens.length ? new Set() : new Set(itens.map((i) => i.idProduto))));
  }

  async function confirmarImportacao(): Promise<void> {
    if (selecionados.size === 0) return setErroImport('Selecione ao menos uma taxa para importar.');
    setErroImport(null);
    try {
      const r = await importar({ idConfiguracaoCardapio: idConfig, idsProduto: [...selecionados] }).unwrap();
      const partes: string[] = [];
      if (r.importados) partes.push(`${r.importados} importada(s)`);
      if (r.atualizados) partes.push(`${r.atualizados} atualizada(s)`);
      setFeedback(`Taxas do Sischef: ${partes.join(', ') || 'nenhuma alteração'}.`);
      fecharImportar();
    } catch (err) {
      setErroImport(
        (err as { data?: { mensagem?: string } })?.data?.mensagem ?? 'Falha ao importar taxas do Sischef.',
      );
    }
  }

  const mensagemErroImportaveis =
    (erroCarregarImportaveis as { data?: { mensagem?: string } } | undefined)?.data?.mensagem ??
    (erroCarregarImportaveis ? 'Não foi possível carregar as taxas do Sischef.' : null);

  function abrirCriar(): void {
    setErro(null);
    setEditor({
      modo: 'criar',
      nomeBairro: '',
      idMunicipio: municipios && municipios.length === 1 ? municipios[0]!.idMunicipio : '',
      valor: '',
      ativo: true,
    });
  }

  function abrirEditar(taxa: TaxaEntregaBairroLoja): void {
    setErro(null);
    // Reencontra o município atendido pelo nome+UF salvos.
    const m = municipios?.find(
      (mu) => mu.nome.toLowerCase() === taxa.municipio.toLowerCase() && mu.uf === taxa.uf,
    );
    setEditor({
      modo: 'editar',
      taxa,
      nomeBairro: taxa.nomeBairro,
      idMunicipio: m?.idMunicipio ?? '',
      valor: String(parseFloat(taxa.valor)),
      ativo: taxa.ativo,
    });
  }

  function fechar(): void {
    setEditor(null);
    setErro(null);
  }

  async function confirmar(): Promise<void> {
    if (!editor) return;
    if (!editor.nomeBairro.trim()) return setErro('Informe o bairro.');
    const municipio = municipios?.find((m) => m.idMunicipio === editor.idMunicipio);
    if (!municipio) return setErro('Selecione o município.');
    const valor = Number(editor.valor);
    if (!Number.isFinite(valor) || valor < 0) return setErro('Valor deve ser maior ou igual a zero.');

    setErro(null);
    try {
      const dados = {
        nomeBairro: editor.nomeBairro.trim(),
        municipio: municipio.nome,
        uf: municipio.uf,
        valor,
        ativo: editor.ativo,
      };
      if (editor.modo === 'criar') {
        await criar({ idConfiguracaoCardapio: idConfig, ...dados }).unwrap();
      } else {
        await atualizar({ id: editor.taxa!.id, dados }).unwrap();
      }
      fechar();
    } catch (err) {
      setErro((err as { data?: { mensagem?: string } })?.data?.mensagem ?? 'Falha ao salvar taxa por bairro.');
    }
  }

  async function alternarAtivo(taxa: TaxaEntregaBairroLoja): Promise<void> {
    try {
      await atualizar({ id: taxa.id, dados: { ativo: !taxa.ativo } }).unwrap();
    } catch {
      // Silencioso — a listagem é a fonte da verdade.
    }
  }

  const salvando = statusCriar.isLoading || statusAtualizar.isLoading;
  const ehEditar = editor?.modo === 'editar';
  const taxas = [...itens].sort((a, b) => a.nomeBairro.localeCompare(b.nomeBairro));
  const semMunicipios = municipios && municipios.length === 0;

  return (
    <>
      <HeaderTela
        titulo="Taxa de entrega"
        descricao={
          porKm
            ? 'A taxa vem da distância entre a loja e o endereço do cliente, pelas faixas abaixo.'
            : 'Defina a taxa de entrega por bairro. O cliente escolhe o bairro no checkout a partir desta lista.'
        }
        acoes={
          porKm ? undefined : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={abrirImportar}
                fullWidth
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Importar do Sischef
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={abrirCriar}
                disabled={semMunicipios}
                fullWidth
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Novo bairro
              </Button>
            </Stack>
          )
        }
      />

      {/* A escolha muda o que o cliente vê no checkout, então fica no topo da
          tela, e não escondida na configuração geral. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControl disabled={statusEstrategia.isLoading}>
          <FormLabel sx={{ typography: 'subtitle2', mb: 1 }}>Como cobrar a entrega</FormLabel>

          <RadioGroup
            value={porKm ? 'KM' : 'BAIRRO'}
            onChange={(_e, valor) => {
              if (valor === (porKm ? 'KM' : 'BAIRRO')) return;
              void salvarConfiguracao({ id: idConfig, dados: { estrategiaTaxa: valor } });
            }}
          >
            <FormControlLabel
              value="BAIRRO"
              control={<Radio />}
              sx={{ alignItems: 'flex-start', mb: 1 }}
              label={
                <Box sx={{ pt: 0.75 }}>
                  <Typography variant="body1">Por bairro</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Você cadastra os bairros atendidos e o valor de cada um. Endereço fora da lista não
                    recebe entrega.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              value="KM"
              // Sem a integração Google não há como medir distância, e cobrar por
              // estimativa seria cobrar errado.
              disabled={!diagnostico?.ok || diagnosticando}
              control={<Radio />}
              sx={{ alignItems: 'flex-start' }}
              label={
                <Box sx={{ pt: 0.75 }}>
                  <Typography variant="body1">Por distância (km)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    O valor sai da distância entre a loja e o endereço do cliente, em faixas de km.
                    Depende da integração Google Maps.
                  </Typography>
                  {diagnosticando && (
                    <Typography variant="caption" color="text.secondary">
                      Verificando a integração Google Maps…
                    </Typography>
                  )}
                  {!diagnosticando && !diagnostico?.ok && diagnostico?.mensagem && (
                    <Typography variant="caption" color="error">
                      Indisponível: {diagnostico.mensagem}
                    </Typography>
                  )}
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Só quando a loja JÁ cobra por distância: aí a falha é urgente,
            porque o checkout está sem calcular taxa neste exato momento. */}
        {!diagnosticando && porKm && diagnostico && !diagnostico.ok && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              diagnostico.temChave ? (
                <Button color="inherit" size="small" onClick={() => void retestar({ idConfig, forcar: true })}>
                  Testar de novo
                </Button>
              ) : undefined
            }
          >
            A cobrança por distância está ligada, mas não está funcionando. {diagnostico.mensagem}
          </Alert>
        )}

        {/* Sem chave, a opção por distância fica travada e a tela só dizia onde
            configurar. O caminho até lá tem quatro níveis de menu, então o
            atalho vai junto — e o "já configurei" força o diagnóstico, que fica
            2 min em cache no servidor e não perceberia a chave nova sozinho.
            O quadro NÃO some durante a verificação: sumir e voltar piscando é
            pior que o botão em espera. */}
        {diagnostico && !diagnostico.temChave && (
          <Box sx={{ mt: 2 }}>
            <PainelInfo
              titulo="Falta a chave do Google Maps"
              denso
              itens={[
                <>
                  A cobrança por distância mede a rota pelo Google Maps, e a chave fica na integração da
                  empresa no <strong>Sischef Retaguarda</strong> — a mesma que a loja já usa na
                  roteirização.
                </>,
                <>
                  No Sischef Retaguarda, em <strong>Integrações → Configurações</strong>, clique em{' '}
                  <strong>Configurar Google Maps</strong>. Depois volte aqui e confira.
                </>,
              ]}
            >
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <BotaoConfigurarGoogleMaps variant="contained" />
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => void retestar({ idConfig, forcar: true })}
                  disabled={diagnosticando}
                  startIcon={diagnosticando ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  Já configurei, verificar
                </Button>
              </Stack>
            </PainelInfo>
          </Box>
        )}

        {/* De onde a distância é medida. Só faz sentido para quem cobra por
            distância — quem cobra por bairro não tem o que fazer com isso.
            Ganha um quadro próprio porque é DADO conferível, não recado: se
            este endereço estiver errado, toda faixa de km sai errada junto. */}
        {!diagnosticando && porKm && diagnostico?.ok && (
          <Paper
            variant="outlined"
            sx={{ mt: 2, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 2,
                flexShrink: 0,
                color: 'success.main',
                bgcolor: (t) => alpha(t.palette.success.main, t.palette.mode === 'dark' ? 0.2 : 0.1),
              }}
            >
              <PlaceOutlinedIcon />
            </Box>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Origem do cálculo
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {diagnostico.enderecoNormalizado ?? 'Coordenada cadastrada na unidade'}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {diagnostico.fonteOrigem === 'GEOCODE'
                  ? 'Endereço da empresa no Sischef — a distância até o cliente é medida a partir daqui.'
                  : 'Latitude/longitude da unidade no Sischef — a distância até o cliente é medida a partir daqui. Para mudar o ponto, corrija a coordenada no cadastro.'}
              </Typography>
            </Box>
          </Paper>
        )}

      {porKm && (
        <Box sx={{ mt: 2 }}>
          <PainelInfo
            titulo="Cobrança por distância"
            itens={[
              <>
                A chave do Google Maps <strong>não é configurada aqui</strong>: ela vem do Sischef
                Gerencial, em <strong>Integrações → Configurações → Google Maps</strong>. É a mesma chave
                que a loja já usa na roteirização.
              </>,
              <>
                Confira o <strong>endereço de origem</strong> acima antes de montar as faixas: ele vem do
                cadastro da empresa no Sischef, e se estiver errado todas as taxas saem erradas junto.
              </>,
              <>
                Cada chamada consome a cota da chave, que é <strong>paga pela loja</strong> — por isso a
                taxa é recalculada no fechamento do pedido, e não a cada tecla digitada no endereço.
              </>,
            ]}
          />
        </Box>
      )}
      </Paper>

      {feedback && (
        <Alert severity="success" onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
          {feedback}
        </Alert>
      )}

      {semMunicipios && !porKm && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cadastre os municípios atendidos na tela de Configuração antes de definir as taxas por bairro.
        </Alert>
      )}

      {porKm && <FaixasKm idConfig={idConfig} />}

      {!porKm && (
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Bairro</TableCell>
                <TableCell>Município</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taxas.map((t) => (
                <TableRow key={t.id} sx={{ opacity: t.ativo ? 1 : 0.6 }}>
                  <TableCell>{t.nomeBairro}</TableCell>
                  <TableCell>
                    {t.municipio}/{t.uf}
                  </TableCell>
                  <TableCell align="right">{formatarValor(t.valor)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t.ativo ? 'Ativo' : 'Inativo'}
                      color={t.ativo ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton onClick={() => abrirEditar(t)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t.ativo ? 'Inativar' : 'Reativar'}>
                      <IconButton onClick={() => alternarAtivo(t)} color={t.ativo ? 'error' : 'success'}>
                        {t.ativo ? <DeleteOutlineIcon /> : <CheckCircleOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {taxas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Nenhuma taxa por bairro cadastrada.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <PaginacaoTabela
          total={total}
          pagina={pagina}
          tamanhoPagina={tamanhoPagina}
          aoMudarPagina={setPagina}
          aoMudarTamanho={(t) => {
            setTamanhoPagina(t);
            setPagina(0);
          }}
        />
      </Paper>
      )}

      <Dialog open={!!editor} onClose={fechar} fullWidth maxWidth="sm">
        <DialogTitle>{ehEditar ? 'Editar taxa por bairro' : 'Nova taxa por bairro'}</DialogTitle>
        <DialogContent>
          {editor && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Bairro"
                value={editor.nomeBairro}
                onChange={(e) => setEditor({ ...editor, nomeBairro: e.target.value })}
                fullWidth
                required
              />
              <TextField
                select
                label="Município"
                value={editor.idMunicipio}
                onChange={(e) => setEditor({ ...editor, idMunicipio: Number(e.target.value) })}
                fullWidth
                required
              >
                {(municipios ?? []).map((m) => (
                  <MenuItem key={m.idMunicipio} value={m.idMunicipio}>
                    {m.nome}/{m.uf}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Valor da taxa (R$)"
                value={editor.valor}
                onChange={(e) => setEditor({ ...editor, valor: e.target.value })}
                type="number"
                fullWidth
                required
                inputProps={{ min: 0, step: '0.01' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.ativo}
                    onChange={(e) => setEditor({ ...editor, ativo: e.target.checked })}
                  />
                }
                label={editor.ativo ? 'Ativo' : 'Inativo'}
              />
              {erro && <Alert severity="error">{erro}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmar} disabled={salvando}>
            {salvando ? <CircularProgress size={20} /> : ehEditar ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importAberto} onClose={fecharImportar} fullWidth maxWidth="sm">
        <DialogTitle>Importar taxas do Sischef</DialogTitle>
        <DialogContent>
          {carregandoImportaveis ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : mensagemErroImportaveis ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {mensagemErroImportaveis}
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <DialogContentText>
                Taxas de entrega cadastradas no Sischef. Os bairros importados ficam no município da empresa
                {importaveis?.municipioDestino && (
                  <>
                    {' '}
                    (<strong>{importaveis.municipioDestino.municipio}/{importaveis.municipioDestino.uf}</strong>)
                  </>
                )}
                . Itens já importados apenas têm o valor sincronizado.
              </DialogContentText>

              {(importaveis?.itens.length ?? 0) === 0 ? (
                <Alert severity="info">Nenhuma taxa de entrega (tipo ENTREGA) cadastrada no Sischef.</Alert>
              ) : (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 420 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!!importaveis && selecionados.size === importaveis.itens.length && selecionados.size > 0}
                            indeterminate={selecionados.size > 0 && selecionados.size < (importaveis?.itens.length ?? 0)}
                            onChange={alternarTodos}
                          />
                        </TableCell>
                        <TableCell>Bairro (descrição)</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importaveis?.itens.map((i) => (
                        <TableRow
                          key={i.idProduto}
                          hover
                          onClick={() => alternarSelecao(i.idProduto)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selecionados.has(i.idProduto)} onChange={() => alternarSelecao(i.idProduto)} />
                          </TableCell>
                          <TableCell>{i.descricao}</TableCell>
                          <TableCell align="right">{formatarBRL(i.valor)}</TableCell>
                          <TableCell>
                            {i.jaImportado && (
                              <Chip size="small" variant="outlined" color="info" label="Já importado" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {erroImport && <Alert severity="error">{erroImport}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharImportar} disabled={statusImportar.isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarImportacao}
            disabled={statusImportar.isLoading || selecionados.size === 0}
          >
            {statusImportar.isLoading ? <CircularProgress size={20} /> : `Importar (${selecionados.size})`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
